/* eslint-disable import/no-default-export */
import { HeaderTestIds } from '@trello/test-ids';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { MemberAvatar } from 'app/src/components/MemberAvatar';
import { getMe, hasGold } from 'app/gamma/src/selectors/members';
import { State } from 'app/gamma/src/modules/types';
import React, {
  useCallback,
  FunctionComponent,
  Suspense,
  useState,
  useEffect,
} from 'react';
import { connect } from 'react-redux';
import { MemberModel } from 'app/gamma/src/types/models';
import HeaderButton from './button';
import styles from './header.less';
import classNames from 'classnames';
import { usePopover, Popover, PopoverScreen } from '@trello/nachos/popover';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { forTemplate } from 'app/common/lib/util/i18n';
import { defaultRouter } from 'app/common/router';
import { useLazyComponent } from '@trello/use-lazy-component';
import {
  AtlassianAccountMigrationStageOverlay,
  isStageActionable,
  useAtlassianAccountMigrationStage,
} from 'app/src/components/AtlassianAccountMigrationStage';
import { useAccountSwitcherDismissNewMutationMutation } from 'app/gamma/src/components/popovers/account-switcher/AccountSwitcherDismissNewMutation.generated';
const format = forTemplate('header_member_menu');

interface Props {
  hasMemberData: boolean;
  member: MemberModel | undefined;
}

enum Screen {
  MemberMenu,
  HelpMenu,
  ChangePasswordMenu,
  AddAccountMenu,
}

const HeaderMemberMenuButton: FunctionComponent<Props> = ({
  hasMemberData,
  member,
}) => {
  const {
    name = '',
    username = '',
    id = '',
    avatarSource = undefined,
    isAaMastered = false,
    oneTimeMessagesDismissed = [],
  } = member || {};
  const userTitle = `${name}${username && ` (${username})`}`;
  const [dismissNew] = useAccountSwitcherDismissNewMutationMutation();

  const [isMigrationOverlayOpen, setMigrationOverlayOpen] = useState(false);
  const [isMigratable, setIsMigratable] = useState(false);
  const { stage, refetchStage } = useAtlassianAccountMigrationStage();

  useEffect(() => setIsMigratable(isStageActionable(stage)), [stage]);

  const useAccountSwitcher =
    useFeatureFlag('aa4a.account-switcher', false) && member?.confirmed;
  const popoverTitle = useAccountSwitcher
    ? format('account')
    : `${member?.name} (${member?.username})`;

  const { toggle, push, hide, triggerRef, popoverProps } = usePopover<
    HTMLButtonElement
  >({
    initialScreen: Screen.MemberMenu,
  });
  const onClick = useCallback(() => {
    if (!hasMemberData) {
      return;
    }

    trackUe({
      category: Noun.HEADER,
      verb: Verb.OPENS,
      directObj: Noun.MEMBER_MENU,
      method: 'by using member menu button in header',
    });
    toggle();
  }, [hasMemberData, toggle]);

  const HeaderMemberMenu = useLazyComponent(() =>
    import(
      /* webpackChunkName: "header-member-menu-popover" */ 'app/gamma/src/components/popovers/header-member-menu'
    ),
  );

  const Help = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "header-member-menu-help" */ 'app/gamma/src/components/popovers/help'
      ),
    {
      preload: popoverProps.isVisible,
    },
  );

  const ChangePassword = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "header-member-menu-change-password" */ 'app/gamma/src/components/popovers/change-password'
      ),
    {
      preload: popoverProps.isVisible,
    },
  );

  const AddAccount = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "header-member-menu-add-account" */ 'app/gamma/src/components/popovers/account-switcher/add-account'
      ),
    {
      preload: popoverProps.isVisible && isAaMastered,
    },
  );

  const UpgradeAccount = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "header-member-menu-add-account" */ 'app/gamma/src/components/popovers/account-switcher/upgrade-account'
      ),
    {
      preload: popoverProps.isVisible && !isAaMastered,
    },
  );

  const onClickAddAccount = async () => {
    if (!oneTimeMessagesDismissed?.includes('headerMemberMenuAddAccount')) {
      dismissNew({
        variables: {
          memberId: 'me',
          messageId: `headerMemberMenuAddAccount`,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          addOneTimeMessagesDismissed: {
            id: 'me',
            oneTimeMessagesDismissed: oneTimeMessagesDismissed!.concat([
              `headerMemberMenuAddAccount`,
            ]),
            __typename: 'Member',
          },
        },
      });
    }
    push(Screen.AddAccountMenu);
  };

  return (
    <>
      <HeaderButton
        // this className is needed by Desktop for the My Cards MenuItem
        className={classNames(
          'js-open-header-member-menu',
          styles.memberButton,
        )}
        onClick={onClick}
        ref={triggerRef}
        testId={HeaderTestIds.MemberMenuButton}
        title={userTitle}
        ariaLabel={forTemplate('header_user')('member-menu')}
      >
        <MemberAvatar avatarSource={avatarSource} idMember={id} size={32} />
      </HeaderButton>
      <Popover {...popoverProps} title={`${popoverTitle}`}>
        <Suspense fallback={null}>
          <PopoverScreen
            id={Screen.MemberMenu}
            testId={HeaderTestIds.MemberMenuPopover}
            noHorizontalPadding
          >
            <HeaderMemberMenu
              onClickHelp={() => push(Screen.HelpMenu)}
              onClickChangePassword={() => push(Screen.ChangePasswordMenu)}
              onClickShortcuts={() => {
                defaultRouter.setRoute('/shortcuts');
                hide();
              }}
              onClickMenuLink={hide}
              onClickAddAccount={onClickAddAccount}
              useAccountSwitcher={useAccountSwitcher}
            />
          </PopoverScreen>
          <PopoverScreen
            id={Screen.AddAccountMenu}
            title={format('add-another-account')}
            noHorizontalPadding
          >
            {isAaMastered ? (
              <AddAccount />
            ) : (
              <UpgradeAccount
                onClickUpdateAccount={() => {
                  setMigrationOverlayOpen(true);
                  hide();
                }}
                isMigratable={isMigratable}
              ></UpgradeAccount>
            )}
          </PopoverScreen>
          <PopoverScreen
            id={Screen.HelpMenu}
            title={format('help')}
            size="xlarge"
            noHorizontalPadding
          >
            <Help />
          </PopoverScreen>
          <PopoverScreen id={Screen.ChangePasswordMenu} noHorizontalPadding>
            <ChangePassword />
          </PopoverScreen>
        </Suspense>
      </Popover>
      {isMigrationOverlayOpen && (
        <AtlassianAccountMigrationStageOverlay
          stage={stage}
          attributes={{
            stage,
          }}
          onComplete={() => {
            setMigrationOverlayOpen(false);
            refetchStage();
          }}
          onDismiss={() => setMigrationOverlayOpen(false)}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: State) => {
  return {
    hasMemberData: !!getMe(state),
    member: getMe(state),
    hasCrown: hasGold(state),
  };
};

export default connect(mapStateToProps)(HeaderMemberMenuButton);
